import isNil from 'lodash/isNil';
import { action, makeObservable, observable } from 'mobx';
import { Account } from '../models';
import { APIClient } from '../apiclient/APIClient';
import {
  CreateInvestmentAccountRequest,
  CreateInvestmentAccountResponse,
  FetchOnboardingProgressResponse,
  InvestmentAccountType,
  OnboardingProgress,
  RegisterUserRequest,
  RegisterUserResponse,
  UpdateEntityInformationRequest,
  UpdateEntityInformationResponse,
} from '../apiclient/APIClientInterface';
import {
  AccreditedInvestorAnswers,
  ContactInfo,
  EntityDetails,
  OnboardingFormData,
  OnboardingStateInterface,
} from './OnboardingStoreInterface';

const initialContactInfo: ContactInfo = {
  address_line_1: '',
  address_line_2: '',
  city: '',
  state: '',
  zipcode: '',
  phone_number: '',
  email: '',
};

const initialEntityDetails: EntityDetails = {
  legal_name: '',
  entity_type: null,
  tax_classification: '',
  state_of_registration: '',
  date_of_formation: '',
};

const initialAccreditedAnswers: AccreditedInvestorAnswers = {
  net_worth_over_1m: false,
  income_over_200k: false,
  household_income_over_300k: false,
  finra_license: false,
};

const initialFormData: OnboardingFormData = {
  contactInfo: initialContactInfo,
  entityDetails: initialEntityDetails,
  accreditedAnswers: initialAccreditedAnswers,
};

const initialState: OnboardingStateInterface = {
  currentStep: 'get-started',
  investmentAccountType: null,
  investmentAccount: null,
  formData: initialFormData,
  progress: null,
  isSubmitting: false,
  isFetchingProgress: false,
  progressFetched: false,
};

export default class OnboardingStore {
  private static instance: OnboardingStore | null = null;

  @observable
  state: OnboardingStateInterface = { ...initialState };

  constructor() {
    if (isNil(OnboardingStore.instance)) {
      OnboardingStore.instance = this;
    }

    makeObservable(this);

    // eslint-disable-next-line no-constructor-return
    return OnboardingStore.instance;
  }

  @action
  createInvestmentAccount = async (
    request: CreateInvestmentAccountRequest
  ): Promise<CreateInvestmentAccountResponse> => {
    this.setIsSubmitting(true);

    const progressResponse = await APIClient.fetchOnboardingProgress({});
    if (progressResponse.success && progressResponse.progress) {
      this.setProgress(progressResponse.progress);

      if (request.type === 'Individual' && progressResponse.progress.has_investment_account) {
        this.setIsSubmitting(false);
        return { success: true } as CreateInvestmentAccountResponse;
      }
    }

    const response = await APIClient.createInvestmentAccount(request);

    if (response.success && response.account) {
      this.setInvestmentAccount(response.account);
      this.setInvestmentAccountType(request.type);
    }

    this.setIsSubmitting(false);
    return response;
  };

  @action
  fetchOnboardingProgress = async (): Promise<FetchOnboardingProgressResponse> => {
    this.setIsFetchingProgress(true);
    const response = await APIClient.fetchOnboardingProgress({});

    if (response.success && response.progress) {
      this.setProgress(response.progress);
    }

    this.setProgressFetched(true);
    this.setIsFetchingProgress(false);
    return response;
  };

  @action
  registerUser = async (request: RegisterUserRequest): Promise<RegisterUserResponse> => {
    this.setIsSubmitting(true);
    const response = await APIClient.registerUser(request);
    this.setIsSubmitting(false);
    return response;
  };

  @action
  reset = () => {
    this.state = { ...initialState, formData: { ...initialFormData } };
  };

  @action
  setAccreditedAnswers = (answers: Partial<AccreditedInvestorAnswers>) => {
    this.state.formData.accreditedAnswers = {
      ...this.state.formData.accreditedAnswers,
      ...answers,
    };
  };

  @action
  setContactInfo = (info: Partial<ContactInfo>) => {
    this.state.formData.contactInfo = {
      ...this.state.formData.contactInfo,
      ...info,
    };
  };

  @action
  setEntityDetails = (details: Partial<EntityDetails>) => {
    this.state.formData.entityDetails = {
      ...this.state.formData.entityDetails,
      ...details,
    };
  };

  @action
  setInvestmentAccount = (account: Account | null) => {
    this.state.investmentAccount = account;
  };

  @action
  setInvestmentAccountType = (type: InvestmentAccountType | null) => {
    this.state.investmentAccountType = type;
  };

  @action
  setIsFetchingProgress = (value: boolean) => {
    this.state.isFetchingProgress = value;
  };

  @action
  setIsSubmitting = (value: boolean) => {
    this.state.isSubmitting = value;
  };

  @action
  setProgress = (progress: OnboardingProgress | null) => {
    this.state.progress = progress;
  };

  @action
  setProgressFetched = (value: boolean) => {
    this.state.progressFetched = value;
  };

  @action
  updateEntityInformation = async (
    request: UpdateEntityInformationRequest
  ): Promise<UpdateEntityInformationResponse> => {
    this.setIsSubmitting(true);
    const response = await APIClient.updateEntityInformation(request);
    this.setIsSubmitting(false);
    if (response.success) {
      this.setProgressFetched(false);
    }
    return response;
  };
}
